import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Empty, Input, Modal, Select, Space, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  BookOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import * as api from '../api/client';
import KnowledgeBaseDetail from './KnowledgeBaseDetail';

const { Text, Title } = Typography;

interface KB {
  id: string;
  name: string;
  description?: string;
  status: string;
  workspace_id?: string;
  project_id?: string;
  user_id?: string;
  created_at?: string;
  updated_at?: string;
  document_count?: number;
  chunk_count?: number;
}

type DetailTab = 'overview' | 'documents' | 'import' | 'search' | 'chunks' | 'sharing' | 'settings';

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'green',
  ARCHIVED: 'default',
};

interface Props {
  userId?: string;
}

const KnowledgeBaseManager: React.FC<Props> = ({ userId = '' }) => {
  const [kbs, setKbs] = useState<KB[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'ARCHIVED'>('ALL');
  const [workspaceFilter, setWorkspaceFilter] = useState<string>('ALL');
  const [selectedKbId, setSelectedKbId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('overview');
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const loadKbs = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await api.listKnowledgeBases({ status: undefined, user_id: userId });
      setKbs(res.data.knowledge_bases || []);
    } catch {
      message.error('Failed to load knowledge bases');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadKbs(); }, [loadKbs]);

  const workspaceOptions = useMemo(
    () => Array.from(new Set(kbs.map(kb => kb.workspace_id).filter(Boolean) as string[])),
    [kbs],
  );

  const filtered = useMemo(() => kbs.filter((kb) => {
    if (statusFilter !== 'ALL' && kb.status !== statusFilter) return false;
    if (workspaceFilter !== 'ALL' && (kb.workspace_id || '') !== workspaceFilter) return false;
    if (!filterText.trim()) return true;
    const q = filterText.toLowerCase();
    return kb.name.toLowerCase().includes(q) || (kb.description || '').toLowerCase().includes(q);
  }), [kbs, statusFilter, workspaceFilter, filterText]);

  useEffect(() => {
    if (filtered.length === 0) {
      if (selectedKbId) setSelectedKbId(null);
      return;
    }
    if (!selectedKbId || !filtered.some(kb => kb.id === selectedKbId)) {
      setSelectedKbId(filtered[0].id);
    }
  }, [filtered, selectedKbId]);

  const metrics = useMemo(() => {
    const totalKbs = kbs.length;
    const indexedDocuments = kbs.reduce((sum, kb) => sum + (kb.document_count || 0), 0);
    const chunks = kbs.reduce((sum, kb) => sum + (kb.chunk_count || 0), 0);
    const activeImports = kbs.filter(kb => kb.status === 'ACTIVE').length;
    return { totalKbs, indexedDocuments, chunks, activeImports };
  }, [kbs]);

  const selectedKb = filtered.find(kb => kb.id === selectedKbId) || null;

  const handleCreate = async () => {
    if (!createName.trim()) {
      message.warning('Name is required');
      return;
    }
    setCreating(true);
    try {
      await api.createKnowledgeBase({
        name: createName.trim(),
        description: createDesc.trim(),
        user_id: userId,
      });
      message.success('Knowledge Base created');
      setCreateOpen(false);
      setCreateName('');
      setCreateDesc('');
      await loadKbs();
    } catch {
      message.error('Failed to create Knowledge Base');
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = async (kb: KB, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.deleteKnowledgeBase(kb.id);
      message.success(`"${kb.name}" archived`);
      if (selectedKbId === kb.id) setSelectedKbId(null);
      await loadKbs();
    } catch {
      message.error('Failed to archive knowledge base');
    }
  };

  const openKb = (kbId: string, tab: DetailTab = 'overview') => {
    setSelectedKbId(kbId);
    setDetailTab(tab);
  };

  return (
    <div className="kb-page">
      <div className="kb-header">
        <div className="kb-hero">
          <BookOutlined className="kb-hero-icon" />
          <div>
            <Title level={3} style={{ margin: 0, color: '#e6edf3' }}>Knowledge Bases</Title>
            <Text className="kb-subtitle">
              Import documents, URLs, spreadsheets, and notes so agents can reason against selected knowledge.
            </Text>
          </div>
        </div>
        <Space>
          <Button icon={<UploadOutlined />} onClick={() => selectedKbId && setDetailTab('import')} disabled={!selectedKbId}>
            Import
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Create Knowledge Base
          </Button>
        </Space>
      </div>

      <div className="kb-stats-grid">
        <Card className="kb-stat-card"><Text className="kb-stat-label">Total KBs</Text><Title level={4}>{metrics.totalKbs}</Title></Card>
        <Card className="kb-stat-card"><Text className="kb-stat-label">Indexed Documents</Text><Title level={4}>{metrics.indexedDocuments}</Title></Card>
        <Card className="kb-stat-card"><Text className="kb-stat-label">Chunks</Text><Title level={4}>{metrics.chunks}</Title></Card>
        <Card className="kb-stat-card"><Text className="kb-stat-label">Active Imports</Text><Title level={4}>{metrics.activeImports}</Title></Card>
      </div>

      <div className="kb-filter-bar">
        <Input
          prefix={<SearchOutlined style={{ color: '#8b949e' }} />}
          placeholder="Search knowledge bases by name or description"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          allowClear
        />
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'ALL', label: 'All Statuses' },
            { value: 'ACTIVE', label: 'Active' },
            { value: 'ARCHIVED', label: 'Archived' },
          ]}
          style={{ width: 160 }}
        />
        <Select
          value={workspaceFilter}
          onChange={setWorkspaceFilter}
          options={[
            { value: 'ALL', label: 'All Workspaces' },
            ...workspaceOptions.map((workspace) => ({ value: workspace, label: workspace })),
          ]}
          style={{ width: 180 }}
        />
        <Tooltip title="Refresh">
          <Button icon={<ReloadOutlined />} loading={loading} onClick={loadKbs} />
        </Tooltip>
      </div>

      <div className="kb-card-grid">
        {filtered.length === 0 && !loading ? (
          <div className="kb-empty-block">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span>Create your first Knowledge Base to give Praxys.ai reusable context.</span>}
            />
          </div>
        ) : filtered.map((kb) => {
          const updatedAt = typeof kb.updated_at === 'string' ? kb.updated_at : '';
          const createdAt = typeof kb.created_at === 'string' ? kb.created_at : '';
          const updatedText = (updatedAt || createdAt).slice(0, 19) || 'n/a';
          const isOwner = !!kb.user_id && !!userId && kb.user_id.toLowerCase() === userId.toLowerCase();
          return (
            <Card
              key={kb.id}
              onClick={() => openKb(kb.id)}
              className={`kb-card${selectedKbId === kb.id ? ' kb-card--selected' : ''}`}
              bodyStyle={{ padding: 14 }}
            >
            <div className="kb-card-top">
              <div>
                <Text strong className="kb-card-title">{kb.name}</Text>
                <Text className="kb-card-description">
                  {kb.description || 'No description provided.'}
                </Text>
              </div>
              <Space size={4} wrap>
                {!isOwner && <Tag color="cyan" icon={<TeamOutlined />}>Shared</Tag>}
                <Tag color={STATUS_COLOR[kb.status] || 'default'}>{kb.status}</Tag>
              </Space>
            </div>
            <div className="kb-card-metrics">
              <span>{kb.document_count || 0} docs</span>
              <span>{kb.chunk_count || 0} chunks</span>
              <span>{updatedText}</span>
            </div>
            <Space size={6} className="kb-card-actions">
              <Button size="small" icon={<FolderOpenOutlined />} onClick={(e) => { e.stopPropagation(); openKb(kb.id, 'overview'); }}>
                Open
              </Button>
              <Button size="small" icon={<UploadOutlined />} onClick={(e) => { e.stopPropagation(); openKb(kb.id, 'import'); }}>
                Import
              </Button>
              <Button size="small" icon={<SearchOutlined />} onClick={(e) => { e.stopPropagation(); openKb(kb.id, 'search'); }}>
                Search
              </Button>
              {isOwner && (
                <Button size="small" icon={<TeamOutlined />} onClick={(e) => { e.stopPropagation(); openKb(kb.id, 'sharing'); }}>
                  Share
                </Button>
              )}
              {isOwner && kb.status === 'ACTIVE' && (
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={e => handleArchive(kb, e)}
                >
                  Archive
                </Button>
              )}
            </Space>
            </Card>
          );
        })}
      </div>

      <div className="kb-workspace">
        {selectedKb ? (
          <KnowledgeBaseDetail
            kbId={selectedKb.id}
            userId={userId}
            initialTab={detailTab}
            onBack={() => setSelectedKbId(null)}
            onRefreshList={loadKbs}
          />
        ) : (
          <div className="kb-empty-block">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span>Select a Knowledge Base to open its workspace.</span>}
            />
          </div>
        )}
      </div>

      <Modal
        className="kb-create-modal"
        title={<span style={{ color: '#e6edf3' }}>Create Knowledge Base</span>}
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => { setCreateOpen(false); setCreateName(''); setCreateDesc(''); }}
        confirmLoading={creating}
        okText="Create"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text className="kb-field-label">Name *</Text>
            <Input
              value={createName}
              onChange={e => setCreateName(e.target.value)}
              placeholder="e.g. Product and API Docs"
            />
          </div>
          <div>
            <Text className="kb-field-label">Description</Text>
            <Input.TextArea
              value={createDesc}
              onChange={e => setCreateDesc(e.target.value)}
              placeholder="Optional description"
              rows={3}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default KnowledgeBaseManager;
